import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { ExportMenu } from "../../ui/export-menu";
import { SummaryHeader } from "../../ui/SummaryHeader";
import CustomerLoader from "../../../components/loaders/CustomerLoader";
import { useWholesaleCustomerSales } from "../hooks/useWholesaleCustomerSales";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { formatCurrency } from "../../../utils/formatCurrency";

type CustomerSale = {
  customer_name: string;
  customer_type: string;
  total_sold: number;
  last_invoice_date: string;
};

export function WholesaleCustomerSales() {
  const { data = [], isLoading } = useWholesaleCustomerSales();

  const customerSales: CustomerSale[] = data;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [customerFilter, setCustomerFilter] = useState<string>("");

  const filteredSales = useMemo(() => {
    if (!customerFilter) return customerSales;
    return customerSales.filter((sale) =>
      sale.customer_name.toLowerCase().includes(customerFilter.toLowerCase())
    );
  }, [customerSales, customerFilter]);

  const columns: ColumnDef<CustomerSale>[] = [
    {
      accessorKey: "customer_name",
      header: "Customer Name",
      cell: ({ row }) => row.getValue("customer_name"),
    },
    {
      accessorKey: "customer_type",
      header: "Customer Type",
      cell: ({ row }) => row.getValue("customer_type"),
    },
    {
      accessorKey: "total_sold",
      header: "Total Sold",
      cell: ({ row }) => formatCurrency(row.getValue("total_sold")),
    },
    {
      accessorKey: "last_invoice_date",
      header: "Last Invoice Date",
      cell: ({ row }) => row.getValue("last_invoice_date"),
    },
  ];

  const table = useReactTable({
    data: filteredSales,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <>
      <SummaryHeader title="Wholesale Customer Sales" />
      <Card className="col-span-full">
        <CardHeader>
          <ExportMenu
            data={table.getFilteredRowModel().rows.map((row) => row.original)}
            filename="wholesale-customer-sales"
            buttonVariant="outline"
            buttonSize="sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
              <div className="flex-1">
                <Input
                  placeholder="Filter Customer Name..."
                  value={customerFilter}
                  onChange={(event) => setCustomerFilter(event.target.value)}
                  className="max-w-sm"
                />
              </div>
            </div>
            <div className="rounded-md border">
              {isLoading ? (
                <CustomerLoader variant="region-analysis2" />
              ) : (
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} of{" "}
                {table.getCoreRowModel().rows.length} row(s) displayed.
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
